import { useResponseTypeClasses } from '@heidi/answer-kit';
import { Badge } from '@heidi/design-system/badge';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { focusRing } from '@heidi/design-system/styles';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronRight, ListTodo } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { agentTodoMessages } from '../../../data/projects/timeline/agent-todo/agentTodo.messages';
import { planAnchorElementId } from '../../../data/projects/timeline/agent-todo/planAnchor';

export type PlanCardPhase = 'awaiting' | 'running' | 'declined' | 'stopped' | 'done';

export interface PlanCardFrameProps {
  title: string;
  phase: PlanCardPhase;
  stepCount: number;
  /** Trailing header status while the plan runs. */
  runningStatus?: ReactNode;
  steps: ReactNode;
  children?: ReactNode;
  hasBody: boolean;
  headerAction?: ReactNode;
  footer?: ReactNode;
  footerNote?: ReactNode;
  /** A change folds the body, so a replaced plan never inherits the old expansion. */
  resetKey: string;
  bodyLockedOpen?: boolean;
  labelledByRow?: boolean;
  anchorId?: string;
  testId: string;
}

const SETTLE_BEFORE_COLLAPSE_MS = 350;

const TERMINAL_BADGE = {
  declined: agentTodoMessages.planDeclinedBadge,
  stopped: agentTodoMessages.planStoppedBadge,
  done: agentTodoMessages.planDoneBadge,
} as const;

export const PlanCardFrame = ({
  title,
  phase,
  stepCount,
  runningStatus,
  steps,
  children,
  hasBody,
  headerAction,
  footer,
  footerNote,
  resetKey,
  bodyLockedOpen = false,
  labelledByRow = false,
  anchorId,
  testId,
}: PlanCardFrameProps): JSX.Element => {
  const intl = useIntl();
  // The title is the card's own content, so it reads as a peer of the sentence
  // that introduced it; the count and progress describe the card, a rung down.
  // `TypographyP2` alone is 14px at every density, compact's number hardcoded.
  const { body, meta } = useResponseTypeClasses();
  const titleId = useId();
  const bodyId = useId();
  const [expanded, setExpanded] = useState(false);

  const previousResetKey = useRef(resetKey);
  if (previousResetKey.current !== resetKey) {
    previousResetKey.current = resetKey;
    setExpanded(false);
  }

  // Let the final tick land before folding a plan that just finished.
  useEffect(() => {
    if (phase !== 'done') return;
    const timeout = window.setTimeout(
      () => setExpanded(false),
      SETTLE_BEFORE_COLLAPSE_MS
    );
    return () => window.clearTimeout(timeout);
  }, [phase]);

  const collapsible = hasBody && phase !== 'awaiting' && !bodyLockedOpen;
  const open = !collapsible || expanded;
  const terminalBadge =
    phase === 'declined' || phase === 'stopped' || phase === 'done'
      ? TERMINAL_BADGE[phase]
      : undefined;

  return (
    <div
      className="relative flex w-full flex-col rounded-lg border border-border bg-surface"
      data-testid={testId}
      data-phase={phase}
      id={anchorId ? planAnchorElementId(anchorId) : undefined}
    >
      {anchorId ? (
        <div
          aria-hidden
          data-plan-anchor-sentinel
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
        />
      ) : null}

      <div className="relative flex w-full items-center gap-3 p-3">
        {!labelledByRow ? <FramedIcon size="sm" icon={<ListTodo />} /> : null}
        <TypographyP2
          id={titleId}
          className={cn('min-w-0 flex-1 truncate text-text-primary', body)}
        >
          <FormattedMessage
            {...agentTodoMessages.planTitle}
            values={{
              title,
              label: (chunks) => (
                <span key="label" className="font-semibold">
                  {chunks}
                </span>
              ),
            }}
          />
        </TypographyP2>
        <span className="flex shrink-0 items-center gap-1.5">
          {phase === 'awaiting' && stepCount > 0 ? (
            <TypographyP2 className={cn('tabular-nums text-text-secondary', meta)}>
              {intl.formatMessage(agentTodoMessages.planStepCount, { count: stepCount })}
            </TypographyP2>
          ) : null}
          {phase === 'running' && runningStatus ? (
            <TypographyP2 className={cn('tabular-nums text-text-secondary', meta)}>
              {runningStatus}
            </TypographyP2>
          ) : null}
          {terminalBadge ? (
            <Badge color="neutral" size="sm" className="rounded-sm font-normal">
              {intl.formatMessage(terminalBadge)}
            </Badge>
          ) : null}
          {collapsible ? (
            <ChevronRight
              aria-hidden
              className={cn(
                'size-3.5 text-text-secondary motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out-quart rtl:-scale-x-100',
                open && 'rotate-90 rtl:-rotate-90'
              )}
            />
          ) : null}
        </span>
        {collapsible ? (
          // A sibling overlay rather than a wrapping button keeps the title
          // readable content and leaves the header action clickable above it.
          <button
            type="button"
            onClick={() => setExpanded((previous) => !previous)}
            aria-expanded={open}
            aria-labelledby={titleId}
            aria-controls={bodyId}
            data-testid="plan-card-toggle"
            className={cn('absolute inset-0 cursor-pointer rounded-lg', focusRing)}
          />
        ) : null}
        {headerAction ? (
          <div className="relative flex shrink-0 items-center gap-2">{headerAction}</div>
        ) : null}
      </div>

      {hasBody ? (
        <div
          id={bodyId}
          data-testid="plan-card-steps"
          aria-hidden={!open}
          // @ts-expect-error - inert is a valid HTML attribute but not in React 18 types yet
          inert={open ? undefined : ''}
          className={cn(
            'grid motion-safe:transition-[grid-template-rows,opacity] motion-safe:duration-200 motion-safe:ease-out-quart',
            open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="flex flex-col gap-3 px-3 pb-3">
              {/* Inset so the step rail sits under the header icon. */}
              <div className="ps-2">{steps}</div>
              {children}
            </div>
          </div>
        </div>
      ) : null}

      {footer ? (
        <div className="flex w-full flex-wrap items-center justify-end gap-x-4 gap-y-2 rounded-b-lg border-t border-border bg-surface-accent px-3 py-2.5">
          {footerNote}
          <div className="ms-auto flex shrink-0 items-center gap-2">{footer}</div>
        </div>
      ) : null}
    </div>
  );
};
